import { useEffect, useMemo, useState } from "react";
import { getInferenceMachine } from "../../../services/inferenceMachineService";

/**
 * Collect every `inference_machine` id present in a camera-model tree, fetch the
 * missing ones, and return an `{ id → hostname }` cache. Each unique id is
 * fetched once and never re-fetched; late responses are dropped if the consumer
 * unmounts. Shared by TreePrototype and the camera-model screenshot/dashcam UIs.
 *
 * @param {Array<object>} tree - The store tree (devices → cameras → views).
 * @returns {Object<number, string>} A map from inference-machine id to hostname.
 */
export default function useInferenceMachineCache(tree) {
  const [cache, setCache] = useState({});

  const ids = useMemo(() => {
    const set = new Set();
    (tree || []).forEach((d) => {
      (d.cameras || []).forEach((c) => {
        if (c.inference_machine != null) set.add(c.inference_machine);
      });
    });
    return [...set];
  }, [tree]);

  useEffect(() => {
    const toFetch = ids.filter((id) => !(id in cache));
    if (toFetch.length === 0) return undefined;
    let cancelled = false;
    Promise.all(
      toFetch.map((id) =>
        getInferenceMachine(id)
          .then((r) => [id, r?.data?.machine?.hostname || r?.data?.hostname || `#${id}`])
          .catch(() => [id, `#${id}`])
      )
    ).then((pairs) => {
      if (cancelled) return;
      setCache((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, name]) => {
          next[id] = name;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [ids, cache]);

  return cache;
}
